import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Popup } from 'devextreme-react/popup';
import { Form, SimpleItem, GroupItem, ButtonItem, Label, Item as FormItem } from 'devextreme-react/form';
import { DataGrid, Column, Selection, SearchPanel, Paging, FilterRow, Toolbar, Item, Editing, Button as GridButton, Scrolling } from 'devextreme-react/data-grid';
import { Button, SelectBox, TextArea, TextBox, LoadIndicator, NumberBox, CheckBox, DateBox } from 'devextreme-react';
import { ScrollView } from 'devextreme-react/scroll-view';
import { newToast } from '../../../components';
import { useScreenSize } from '../../../utils/media-query';
import { useAppSelector } from '../../../contexts/hooks';
import { RemisionFormData, EstudioSeleccionado, OrdenEstudioRemision, RemisionData } from '../../../types/types.remissions';
import RemisionesAPI from '../../../api/api.remmisions';
import './ModalRemision.scss';
import './ModalEditRemision.scss';

interface ModalEditRemisionProps {
  visible: boolean;
  remisionId: number | null;
  onClose: (refresh?: boolean) => void;
  wrapperAttr?: { class: string };
}

export const ModalEditRemision: React.FC<ModalEditRemisionProps> = ({ visible, remisionId, onClose, wrapperAttr = { class: '' } }) => {
  // Estados principales
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [remisionData, setRemisionData] = useState<RemisionData | null>(null);
  const [ordenesDisponibles, setOrdenesDisponibles] = useState<OrdenEstudioRemision[]>([]);
  const [estudiosActuales, setEstudiosActuales] = useState<EstudioSeleccionado[]>([]);
  const [selectedRows, setSelectedRows] = useState<OrdenEstudioRemision[]>([]);

  // Datos del formulario
  const [formData, setFormData] = useState<RemisionFormData>({
    laboratorioId: null,
    fechaInicio: null,
    fechaFin: null,
    temperaturaNevera: '',
    temperaturaAmbiente: '',
    notas: '',
    responsableEnvio: '',
  });

  // Referencias
  const formRef = useRef<Form>(null);
  const gridOrdenesRef = useRef<DataGrid>(null);
  const gridActualesRef = useRef<DataGrid>(null);
  const loadedRemisionId = useRef<number | null>(null);

  // Screen size
  const { isSmall, isXSmall } = useScreenSize();

  // API
  const api = RemisionesAPI();

  // Obtener laboratorios desde Redux
  const laboratorios = useAppSelector((state) => state.configuration.Data?.ComboBox?.find((combo) => combo.Name === 'SysA_LaboratoriosRemisiones')?.Data || []);

  // Cargar datos de la remisión
  const loadRemisionData = useCallback(
    async (id: number) => {
      setLoading(true);

      try {
        const data = await api.getRemisionById(id);

        if (data) {
          setRemisionData(data);
          loadedRemisionId.current = id;

          const hoy = new Date();
          const hace7Dias = new Date(hoy);
          hace7Dias.setDate(hoy.getDate() - 7);

          // Establecer datos del formulario
          setFormData({
            laboratorioId: data.Rm_Lr_Id,
            fechaInicio: data.Rm_FechaInicio || hoy, // ✅ Por defecto últimos 7 días
            fechaFin: data.Rm_FechaFin || hoy, // ✅ Por defecto hasta hoy
            temperaturaNevera: data.Rm_TemperaturaNevera || '',
            temperaturaAmbiente: data.Rm_TemperaturaAmbiente || '',
            notas: data.Rm_Notas || '',
            responsableEnvio: data.Rm_ResponsableEnvio || '',
          });
          // Convertir detalles actuales a formato editable
          const estudiosActualesFormateados =
            data.DetalleRemisiones?.map((detalle: any) => ({
              // Datos del detalle de remisión
              Rmd_Id: detalle.Rmd_Id,
              Rmd_Rm_Id: detalle.Rmd_Rm_Id,
              Rmd_Cantidad: detalle.Rmd_Cantidad || 1,
              Rmd_Urgente: detalle.Rmd_Urgente || false,
              Rmd_Nota: detalle.Rmd_Nota || '',
              Rmd_ResultadoRecibido: detalle.Rmd_ResultadoRecibido || false,
              Rmd_FechaResultado: detalle.Rmd_FechaResultado,

              // Datos de la orden
              Ord_Id: detalle.Orden?.Ord_Id || 0,
              Ord_Numero: detalle.Orden?.Ord_Numero || '',
              Ord_FechaCreacion: detalle.Orden?.Ord_FechaCreacion || new Date(),

              // Datos del paciente
              Paciente_Nombre: detalle.Orden?.Paciente ? `${detalle.Orden.Paciente.Pc_Nombre} ${detalle.Orden.Paciente.Pc_Apellido1} ${detalle.Orden.Paciente.Pc_Apellido2 || ''}`.trim() : '',
              Paciente_Identificacion: detalle.Orden?.Paciente?.Pc_Identificacion || '',

              // Datos del estudio
              Es_Id: detalle.Estudio?.Es_Id || 0,
              Es_Codigo: detalle.Estudio?.Es_Codigo || '',
              Es_Nombre: detalle.Estudio?.Es_Nombre || '',
              Area_Nombre: detalle.Estudio?.SysA_AreasAnalisis?.Aa_Nombre || '',

              // Usar un ID único para el grid (combinar Ord_Id y Es_Id)
              Roe_Id: `${detalle.Orden?.Ord_Id || 0}_${detalle.Estudio?.Es_Id || 0}_${detalle.Rmd_Id}`,

              // Campos adicionales
              Tiene_Resultado: false, // Calculado posteriormente si es necesario
              Fecha_Resultado: null,

              // Marcar como estudio existente
              _isExisting: true,
              _originalRmdId: detalle.Rmd_Id,
            })) || [];

          setEstudiosActuales(estudiosActualesFormateados);

          setOrdenesDisponibles([]);
        } else {
          newToast('ERROR', 'No se pudo cargar la información de la remisión', 4);
          onClose();
        }
      } catch (error) {
        console.error('Error cargando remisión:', error);
        newToast('ERROR', 'Error al cargar los datos de la remisión', 4);
        onClose();
      } finally {
        setLoading(false);
      }
    },
    [api, onClose]
  );

  // Cargar órdenes pendientes para el laboratorio
  const loadOrdenesPendientes = async (laboratorioId: number, fechaInicio?: Date | null, fechaFin?: Date | null) => {
    try {
      const ordenes = await api.getOrdenesPendientesRemision(laboratorioId, fechaInicio, fechaFin);

      if (ordenes && ordenes.length > 0) {
        setOrdenesDisponibles(ordenes);
      } else {
        setOrdenesDisponibles([]);
      }
    } catch (error) {
      console.error('Error cargando órdenes pendientes:', error);
      newToast('ERROR', 'Error al cargar órdenes pendientes', 4);
      setOrdenesDisponibles([]);
    }
  };

  // Cargar datos cuando se abre el modal
  useEffect(() => {
    if (visible && remisionId) {
      if (loadedRemisionId.current !== remisionId) {
        loadRemisionData(remisionId);
      }
    } else if (!visible) {
      resetForm();
    }
  }, [visible, remisionId, loadRemisionData]);

  // Reset form
  const resetForm = useCallback(() => {
    setRemisionData(null);
    setFormData({
      laboratorioId: null,
      temperaturaNevera: '',
      temperaturaAmbiente: '',
      notas: '',
      responsableEnvio: '',
      fechaInicio: null,
      fechaFin: null,
    });
    setEstudiosActuales([]);
    setOrdenesDisponibles([]);
    setSelectedRows([]);
    loadedRemisionId.current = null;
  }, []);

  // Manejar cambios en el laboratorio
  const handleLaboratorioChange = useCallback(async (e: any) => {
    const nuevoLaboratorioId = e.value;
    setFormData((prev) => ({ ...prev, laboratorioId: nuevoLaboratorioId }));
  }, []);

  const handleBuscarEstudios = useCallback(async () => {
    // Validaciones
    if (!formData.laboratorioId) {
      newToast('WARNING', 'Debe seleccionar un laboratorio', 4);
      return;
    }
    if (!formData.fechaInicio) {
      newToast('WARNING', 'Debe seleccionar la fecha de inicio', 4);
      return;
    }
    if (!formData.fechaFin) {
      newToast('WARNING', 'Debe seleccionar la fecha de fin', 4);
      return;
    }
    if (formData.fechaInicio > formData.fechaFin) {
      newToast('WARNING', 'La fecha de inicio no puede ser mayor a la fecha de fin', 4);
      return;
    }

    // ✅ BUSCAR CON FECHAS
    setLoading(true);
    try {
      await loadOrdenesPendientes(formData.laboratorioId, formData.fechaInicio, formData.fechaFin);
      newToast('SUCCESS', `Se encontraron ${ordenesDisponibles.length} estudios disponibles`, 3);
    } catch (error) {
      console.error('Error al buscar estudios:', error);
      newToast('ERROR', 'Error al buscar estudios disponibles', 4);
    } finally {
      setLoading(false);
    }
  }, [formData.laboratorioId, formData.fechaInicio, formData.fechaFin, ordenesDisponibles.length]);

  // Agregar estudios seleccionados
  const handleAgregarSeleccionados = useCallback(() => {
    if (selectedRows.length === 0) {
      newToast('WARNING', 'Debe seleccionar al menos un estudio', 3);
      return;
    }

    let agregados = 0;
    let yaExistentes = 0;
    const idsARemover: number[] = [];

    selectedRows.forEach((orden) => {
      // Verificar si ya existe por Ord_Id y Es_Id (no por Roe_Id)
      const yaSeleccionado = estudiosActuales.find((e) => e.Ord_Id === orden.Ord_Id && e.Es_Id === orden.Es_Id);

      if (!yaSeleccionado) {
        const nuevoEstudio: EstudioSeleccionado = {
          ...orden,
          Rmd_Cantidad: 1,
          Rmd_Urgente: false,
          Rmd_Nota: '',
          // Generar un ID único temporal para nuevos estudios (usar número negativo)
          Roe_Id: -(Date.now() + Math.floor(Math.random() * 1000)),
          _isExisting: false,
          _originalRmdId: null,
        };
        setEstudiosActuales((prev) => [...prev, nuevoEstudio]);
        idsARemover.push(orden.Roe_Id);
        agregados++;
      } else {
        yaExistentes++;
      }
    });

    // Remover los estudios agregados de ordenesDisponibles
    if (idsARemover.length > 0) {
      setOrdenesDisponibles((prev) => prev.filter((orden) => !idsARemover.includes(orden.Roe_Id)));
    }

    // Limpiar selección
    setSelectedRows([]);
    gridOrdenesRef.current?.instance?.clearSelection();

    // Mostrar mensajes
    if (agregados > 0) {
      newToast('SUCCESS', `${agregados} estudio(s) agregado(s) exitosamente`, 3);
    }
    if (yaExistentes > 0) {
      newToast('INFO', `${yaExistentes} estudio(s) ya estaba(n) en la remisión`, 3);
    }
  }, [selectedRows, estudiosActuales]);

  // Remover estudio
  const handleRemoveEstudio = useCallback(
    (roeId: number) => {
      // Encontrar el estudio a remover
      const estudioARemover = estudiosActuales.find((e) => e.Roe_Id === roeId);

      // Si es un estudio que no existía previamente (_isExisting = false), devolverlo a disponibles
      if (estudioARemover && !estudioARemover._isExisting) {
        // Crear el objeto para volver a agregarlo a ordenesDisponibles
        const ordenARestaurar: OrdenEstudioRemision = {
          Roe_Id: estudioARemover.Roe_Id,
          Ord_Id: estudioARemover.Ord_Id,
          Ord_Numero: estudioARemover.Ord_Numero,
          Ord_FechaCreacion: estudioARemover.Ord_FechaCreacion,
          Paciente_Nombre: estudioARemover.Paciente_Nombre,
          Paciente_Identificacion: estudioARemover.Paciente_Identificacion,
          Es_Id: estudioARemover.Es_Id,
          Es_Codigo: estudioARemover.Es_Codigo,
          Es_Nombre: estudioARemover.Es_Nombre,
          Area_Nombre: estudioARemover.Area_Nombre,
          Tiene_Resultado: estudioARemover.Tiene_Resultado || false,
          Fecha_Resultado: estudioARemover.Fecha_Resultado || null,
        };

        setOrdenesDisponibles((prev) => [...prev, ordenARestaurar]);
      }

      // Remover el estudio de estudiosActuales
      setEstudiosActuales((prev) => prev.filter((e) => e.Roe_Id !== roeId));
      newToast('INFO', 'Estudio removido', 2);
    },
    [estudiosActuales]
  );

  // Manejar selección en el grid
  const handleSelectionChanged = useCallback((e: any) => {
    setSelectedRows(e.selectedRowsData || []);
  }, []);

  // Guardar cambios
  const handleSave = async () => {
    if (!remisionData) {
      newToast('ERROR', 'No hay datos de remisión para guardar', 4);
      return;
    }

    // Validaciones
    if (!formData.laboratorioId) {
      newToast('WARNING', 'Debe seleccionar un laboratorio', 4);
      return;
    }

    if (estudiosActuales.length === 0) {
      newToast('WARNING', 'La remisión debe tener al menos un estudio', 4);
      return;
    }

    setSaving(true);
    try {
      // Preparar TODOS los estudios actuales (existentes + nuevos)
      const todosLosEstudios = estudiosActuales.map((estudio) => {
        // Determinar si es un estudio existente o nuevo
        const esEstudioExistente = remisionData.DetalleRemisiones?.some((original) => original.Rmd_Ord_Id === estudio.Ord_Id && original.Rmd_Es_Id === estudio.Es_Id);

        return {
          // Para estudios existentes, incluir el ID original si existe
          Rmd_Id: esEstudioExistente ? remisionData.DetalleRemisiones?.find((original) => original.Rmd_Ord_Id === estudio.Ord_Id && original.Rmd_Es_Id === estudio.Es_Id)?.Rmd_Id : undefined,
          Rmd_Es_Id: estudio.Es_Id,
          Rmd_Ord_Id: estudio.Ord_Id,
          Rmd_Cantidad: estudio.Rmd_Cantidad || 1,
          Rmd_Urgente: estudio.Rmd_Urgente || false,
          Rmd_Nota: estudio.Rmd_Nota || null,
          // Marcar si es nuevo o existente (para debugging)
          _isNew: !esEstudioExistente,
        };
      });

      // Preparar datos para actualización
      const updateData = {
        Rm_Id: remisionData.Rm_Id,
        Rm_Lr_Id: formData.laboratorioId,
        Rm_TemperaturaNevera: formData.temperaturaNevera || null,
        Rm_TemperaturaAmbiente: formData.temperaturaAmbiente || null,
        Rm_Notas: formData.notas || null,
        Rm_ResponsableEnvio: formData.responsableEnvio || null,
        // Enviar TODOS los estudios (el backend determinará qué hacer con cada uno)
        DetalleRemisiones: todosLosEstudios,
      };

      const result = await api.updateRemisionCompleta(updateData, setSaving, (data) => {
        newToast('SUCCESS', 'Remisión actualizada correctamente', 3);
        onClose(true);
      });
    } catch (error) {
      console.error('Error actualizando remisión:', error);
      newToast('ERROR', 'Error al actualizar la remisión', 4);
      setSaving(false);
    }
  };

  // Paciente compacto: nombre y documento debajo, sin aumentar el alto de la celda
  const renderPaciente = (cellData: any) => {
    const nombre = cellData.data?.Paciente_Nombre?.trim() || '—';
    const doc = cellData.data?.Paciente_Identificacion;
    return (
      <div className='cell-paciente'>
        <span className='cell-paciente-nombre'>{nombre}</span>
        {doc && <span className='cell-paciente-doc'>DOC: {doc}</span>}
      </div>
    );
  };

  if (!visible || !remisionData) {
    return null;
  }

  return (
    <Popup visible={visible} onHiding={() => onClose(false)} dragEnabled={true} hideOnOutsideClick={false} showCloseButton={true} showTitle={true} title={`Editar Remisión No. ${remisionData.Rm_Id}`} fullScreen={isXSmall} wrapperAttr={{ ...wrapperAttr, class: `${wrapperAttr?.class} modal-edit-remision-wrapper` }} height='98%' width={'98%'}>
      <div className='modal-edit-remision'>
        {/* Formulario principal */}
        <div className='modal-form-section'>
          <div className='form-container'>
            <Form ref={formRef} colCount={isSmall ? 1 : 2}>
              {/* PRIMERA FILA: Laboratorio y Responsable */}
              <GroupItem colCount={isSmall ? 1 : 2}>
                <FormItem>
                  <SelectBox label='Laboratorio de Remisión *' stylingMode='outlined' labelMode='static' dataSource={laboratorios} displayExpr='Lr_Nombre' valueExpr='Lr_Id' value={formData.laboratorioId} onValueChanged={(e) => setFormData((prev) => ({ ...prev, laboratorioId: e.value }))} searchEnabled={true} />
                </FormItem>

                <FormItem>
                  <TextBox label='Responsable de Envío' stylingMode='outlined' labelMode='static' value={formData.responsableEnvio} onValueChanged={(e) => setFormData((prev) => ({ ...prev, responsableEnvio: e.value }))} />
                </FormItem>
              </GroupItem>

              {/* SEGUNDA FILA: Temperaturas */}
              <GroupItem colCount={isSmall ? 1 : 2}>
                <FormItem>
                  <TextBox label='Temperatura Nevera' stylingMode='outlined' labelMode='static' value={formData.temperaturaNevera} onValueChanged={(e) => setFormData((prev) => ({ ...prev, temperaturaNevera: e.value }))} />
                </FormItem>

                <FormItem>
                  <TextBox label='Temperatura Ambiente' stylingMode='outlined' labelMode='static' value={formData.temperaturaAmbiente} onValueChanged={(e) => setFormData((prev) => ({ ...prev, temperaturaAmbiente: e.value }))} />
                </FormItem>
              </GroupItem>

              {/* NOTAS */}
              <GroupItem colCount={1}>
                <FormItem>
                  <TextArea label='Notas' stylingMode='outlined' labelMode='static' value={formData.notas} onValueChanged={(e) => setFormData((prev) => ({ ...prev, notas: e.value }))} height={60} />
                </FormItem>
              </GroupItem>

              {/* FECHAS DE BÚSQUEDA */}
              <GroupItem caption='Búsqueda de Estudios' colCount={isSmall ? 1 : 3}>
                <FormItem>
                  <DateBox label='Fecha Inicio *' stylingMode='outlined' labelMode='static' value={formData.fechaInicio || undefined} onValueChanged={(e) => setFormData((prev) => ({ ...prev, fechaInicio: e.value }))} displayFormat='dd/MM/yyyy' max={formData.fechaFin || undefined} showClearButton={false} />
                </FormItem>

                <FormItem>
                  <DateBox label='Fecha Fin *' stylingMode='outlined' labelMode='static' value={formData.fechaFin || undefined} onValueChanged={(e) => setFormData((prev) => ({ ...prev, fechaFin: e.value }))} displayFormat='dd/MM/yyyy' min={formData.fechaInicio || undefined} max={new Date()} showClearButton={false} />
                </FormItem>

                <FormItem>
                  <Button text='Buscar Estudios' type='default' stylingMode='contained' onClick={handleBuscarEstudios} disabled={loading} />
                </FormItem>
              </GroupItem>
            </Form>
          </div>
        </div>

        {/* Sección de grids */}
        <div className='modal-grids-section'>
          <ScrollView height='100%' width='100%' showScrollbar='onScroll' scrollByContent={true} scrollByThumb={true} bounceEnabled={false}>
            <div className='grids-container'>
              {/* Estudios actuales */}
              <div className='grid-wrapper'>
                <div className='grid-header'>
                  <h5>Estudios en la Remisión ({estudiosActuales.length})</h5>
                </div>

                <div className='grid-content'>
                  <DataGrid ref={gridActualesRef} dataSource={estudiosActuales} keyExpr='Roe_Id' height='100%' showBorders={false} rowAlternationEnabled={true} columnAutoWidth={false} width='100%'>
                    <Editing mode='cell' allowUpdating={true} allowDeleting={false} />
                    <Scrolling columnRenderingMode='standard' mode='virtual' useNative={true} scrollByContent={true} />
                    <SearchPanel visible={true} placeholder='Buscar estudios...' />
                    <Paging enabled={true} pageSize={10} />

                    <Column dataField='Ord_Numero' caption='ORDEN' width={90} allowEditing={false} />
                    <Column dataField='Paciente_Nombre' caption='PACIENTE' width={200} allowEditing={false} cellRender={renderPaciente} />
                    <Column
                      caption='ESTUDIO'
                      minWidth={250}
                      allowEditing={false}
                      calculateCellValue={(rowData: any) => `${rowData.Es_Codigo} - ${rowData.Es_Nombre}`}
                      calculateFilterExpression={(filterValue: any, selectedFilterOperation: any) => {
                        return [['Es_Codigo', 'contains', filterValue], 'or', ['Es_Nombre', 'contains', filterValue]];
                      }}
                    />
                    <Column dataField='Rmd_Cantidad' caption='CANT.' dataType='number' alignment='center' width={58} editorOptions={{ min: 1, max: 999 }} />
                    <Column dataField='Rmd_Urgente' caption='URGENTE' dataType='boolean' width={83} />
                    <Column dataField='Rmd_Nota' caption='NOTA' minWidth={100} editorOptions={{ placeholder: 'Observaciones...' }} />
                    <Column type='buttons' caption='ACCIÓN' width={75}>
                      <GridButton text='Quitar' icon='trash' hint='Quitar estudio de la remisión' onClick={(e) => handleRemoveEstudio(e.row?.data?.Roe_Id)} />
                    </Column>
                  </DataGrid>
                </div>
              </div>

              {/* Estudios disponibles */}
              <div className='grid-wrapper estudios-disponibles'>
                <div className='grid-header'>
                  <h5>Estudios Disponibles ({ordenesDisponibles.length})</h5>
                  {selectedRows.length > 0 && <Button text={`Agregar Seleccionados (${selectedRows.length})`} type='default' stylingMode='contained' icon='plus' onClick={handleAgregarSeleccionados} />}
                </div>

                <div className='grid-content'>
                  <DataGrid ref={gridOrdenesRef} dataSource={ordenesDisponibles} keyExpr='Roe_Id' height='100%' showBorders={false} rowAlternationEnabled={true} onSelectionChanged={handleSelectionChanged} columnAutoWidth={false} width='100%'>
                    <Selection mode='multiple' />
                    <Scrolling columnRenderingMode='standard' mode='virtual' useNative={true} scrollByContent={true} />
                    <SearchPanel visible={true} placeholder='Buscar órdenes...' />
                    <FilterRow visible={true} />
                    <Paging enabled={true} pageSize={10} />

                    <Column dataField='Ord_Numero' caption='ORDEN' width={100} />
                    <Column dataField='Paciente_Nombre' caption='PACIENTE' width={200} cellRender={renderPaciente} />
                    <Column
                      caption='ESTUDIO'
                      minWidth={250}
                      calculateCellValue={(rowData: any) => `${rowData.Es_Codigo} - ${rowData.Es_Nombre}`}
                      calculateFilterExpression={(filterValue: any, selectedFilterOperation: any) => {
                        return [['Es_Codigo', 'contains', filterValue], 'or', ['Es_Nombre', 'contains', filterValue]];
                      }}
                    />
                    <Column dataField='Area_Nombre' caption='ÁREA' width={120} />
                  </DataGrid>
                </div>
              </div>
            </div>
          </ScrollView>
        </div>

        {/* Footer */}
        <div className='modal-footer-actions'>
          <div className='footer-info'>
            {estudiosActuales.length > 0 && (
              <>
                <div className='info-item'>
                  <strong>{estudiosActuales.length}</strong> estudio(s) en la remisión
                </div>
                <div className='info-item'>
                  <strong>{estudiosActuales.filter((e) => e.Rmd_Urgente).length}</strong> urgente(s)
                </div>
                <div className='info-item'>
                  <strong>{estudiosActuales.filter((e) => e.Rmd_Nota && e.Rmd_Nota.trim()).length}</strong> con notas
                </div>
              </>
            )}
          </div>

          <div className='footer-buttons'>
            <Button text='Cancelar' type='normal' stylingMode='outlined' onClick={() => onClose(false)} disabled={saving} />
            <Button text={saving ? 'Guardando...' : 'Guardar Cambios'} type='default' stylingMode='contained' onClick={handleSave} disabled={saving} />
          </div>
        </div>

        {saving && (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.8)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
            }}>
            <LoadIndicator height={40} width={40} />
          </div>
        )}
      </div>
    </Popup>
  );
};
